// ── abcAnalysis.js — Pareto 80/20 매출 누적 ABC 분류 ──
// A: 누적 매출 80%까지 (집중 관리)
// B: 누적 80~95% (보조)
// C: 누적 95~100% (정리 후보)
//
// 입력: enrichedMenus[] (actualSales 필수)
// 출력: classified[] — { ...원본, abcClass: 'A'|'B'|'C', cumulativeShare, rank }

export const ABC_META = {
  A: { label: 'A', color: '#18181B', bg: '#F4F4F5', desc: '매출 상위 80% — 집중 관리' },
  B: { label: 'B', color: '#EA580C', bg: '#F4F4F5', desc: '매출 80~95% — 보조 관리' },
  C: { label: 'C', color: '#9ca3af', bg: '#f3f4f6', desc: '매출 95~100% — 정리 후보' },
};

/**
 * 매출(revenue) 기준 ABC 분류
 * @param {Array} menus  enrichedMenus (actualSales.revenue 필수)
 * @param {object} options
 *   - thresholdA: A 임계값 누적비율 (기본 0.80)
 *   - thresholdB: B 임계값 (기본 0.95)
 * @returns {object} { menus: classified, summary }
 */
export function classifyAbc(menus, options = {}) {
  const { thresholdA = 0.80, thresholdB = 0.95 } = options;

  const evaluable = (menus || []).filter(m =>
    m && m.actualSales && m.actualSales.revenue > 0
  );

  if (evaluable.length === 0) {
    return {
      menus: [],
      summary: { evaluableCount: 0, totalRevenue: 0, insufficientData: true },
    };
  }

  // 매출 내림차순 정렬
  const sorted = [...evaluable].sort(
    (a, b) => (b.actualSales.revenue || 0) - (a.actualSales.revenue || 0)
  );
  const totalRevenue = sorted.reduce((s, m) => s + (m.actualSales.revenue || 0), 0);

  let cumulative = 0;
  const classified = sorted.map((m, idx) => {
    const share = m.actualSales.revenue / totalRevenue;
    cumulative += share;
    let abcClass;
    if (cumulative <= thresholdA + 0.0001) abcClass = 'A';
    else if (cumulative <= thresholdB + 0.0001) abcClass = 'B';
    else abcClass = 'C';
    return {
      ...m,
      abcClass,
      revenueShare: share,
      cumulativeShare: cumulative,
      rank: idx + 1,
    };
  });

  // 사분면별 카운트 + 매출 합계
  const counts = { A: 0, B: 0, C: 0 };
  const revenueByClass = { A: 0, B: 0, C: 0 };
  for (const m of classified) {
    counts[m.abcClass] += 1;
    revenueByClass[m.abcClass] += m.actualSales.revenue;
  }

  return {
    menus: classified,
    summary: {
      evaluableCount: classified.length,
      totalRevenue,
      counts,
      revenueByClass,
      aShareOfMenu: classified.length > 0 ? counts.A / classified.length : 0,
      aShareOfRevenue: totalRevenue > 0 ? revenueByClass.A / totalRevenue : 0,
    },
  };
}

/**
 * 누적 매출 곡선 데이터 (Pareto 그래프용)
 * @param {Array} classified
 * @returns {Array} [{ rank, cumulativeShare, name, abcClass }]
 */
export function paretoCurve(classified) {
  return (classified || []).map(m => ({
    rank: m.rank,
    cumulativeShare: m.cumulativeShare,
    name: m.name,
    abcClass: m.abcClass,
  }));
}
